import { apiService } from './ApiService';
import {
  InspectionLineItemResponse,
  InspectionResponse,
  InspectionChecklistItemResponse,
  AttachmentResponse,
  ContractDetailsResponse,
} from '@/types/response/InspectionResultResponses';
import {
  FrequenciesResponse,
  InspectionMethodsResponse,
  ExpertiseAreasResponse,
  InspectorsResponse,
  SitesResponse,
  UsersResponse,
} from '@/types/response/lookupResponses';

// ─── Report Options ──────────────────────────────────────────────────────────

export interface ReportOptions {
  lineItemDetail: boolean;
  background: boolean;
  frequency: boolean;
  scoringCriteria: boolean;
  checklistComments: boolean;
  attachmentDescription: boolean;
}

export const DEFAULT_REPORT_OPTIONS: ReportOptions = {
  lineItemDetail: true,
  background: true,
  frequency: true,
  scoringCriteria: false,
  checklistComments: true,
  attachmentDescription: true,
};

// ─── Lookup helpers ──────────────────────────────────────────────────────────

interface LookupMaps {
  inspectors: Map<number, InspectorsResponse>;
  sites: Map<number, SitesResponse>;
  expertiseAreas: Map<number, ExpertiseAreasResponse>;
  inspectionMethods: Map<number, InspectionMethodsResponse>;
  frequencies: Map<number, FrequenciesResponse>;
  users: Map<number, UsersResponse>;
}

function getLookupValue(id: number | null | undefined, map: Map<number, { Name: string }> | null): string {
  if (id == null || !map) return '';
  return map.get(id)?.Name ?? '';
}

// ─── Date formatting ─────────────────────────────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = months[d.getMonth()];
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch { return ''; }
}

function formatTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    let h = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${h}:${mins} ${ampm}`;
  } catch { return ''; }
}

// ─── Escape HTML ─────────────────────────────────────────────────────────────

function esc(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function nl2br(text: string | null | undefined): string {
  if (!text) return '';
  return esc(text).replace(/\n/g, '<br>');
}

// ─── File type detection ─────────────────────────────────────────────────────

const IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'gif', 'png', 'webp', 'bmp'];

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function isImageFile(filename: string): boolean {
  return IMAGE_EXTENSIONS.includes(getFileExtension(filename));
}

function getFileTypeIcon(filename: string, link?: string | null): string {
  if (link) return '🔗';
  const ext = getFileExtension(filename);
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['doc', 'docx', 'rtf'].includes(ext)) return '📝';
  if (['ppt', 'pptx', 'pptm'].includes(ext)) return '📽️';
  if (ext === 'pdf') return '📄';
  if (ext === 'txt') return '📃';
  return '📎';
}

// ─── Service ─────────────────────────────────────────────────────────────────

class ReportGenerationService {

  /**
   * Fetch data, build HTML, and open a print window.
   */
  public async generateReport(
    inspectionResultIds: number[],
    options: ReportOptions,
    onProgress?: (msg: string) => void,
  ): Promise<void> {
    if (inspectionResultIds.length === 0) throw new Error('No inspection results selected.');

    onProgress?.('Loading inspection data…');
    const [lineItems, contractDetails] = await Promise.all([
      apiService.getContractInspectionResultsForReport(inspectionResultIds),
      apiService.getContractDetails().catch(() => null),
    ]);

    onProgress?.('Loading lookup data…');
    const lookups = await this.loadLookups();

    onProgress?.('Building report…');
    const html = this.buildReportHtml(lineItems, contractDetails, lookups, options);

    onProgress?.('Opening print window…');
    this.openPrintWindow(html, contractDetails?.Name ?? 'Inspection Result');
  }

  /**
   * Fetch data, build HTML, render to a hidden element and download as PDF via html2pdf.js.
   */
  public async downloadReportAsPdf(
    inspectionResultIds: number[],
    options: ReportOptions,
    filename: string,
    onProgress?: (msg: string) => void,
  ): Promise<void> {
    if (inspectionResultIds.length === 0) throw new Error('No inspection results selected.');

    onProgress?.('Loading inspection data…');
    const [lineItems, contractDetails] = await Promise.all([
      apiService.getContractInspectionResultsForReport(inspectionResultIds),
      apiService.getContractDetails().catch(() => null),
    ]);

    onProgress?.('Loading lookup data…');
    const lookups = await this.loadLookups();

    onProgress?.('Building report…');
    const contentHtml = this.buildReportHtml(lineItems, contractDetails, lookups, options);
    const title = contractDetails?.Name ?? 'Inspection Result';
    const fullHtml = this.buildFullHtmlDocument(contentHtml, title);

    onProgress?.('Rendering PDF…');
    await this.renderHtmlToPdf(fullHtml, filename);
  }

  /**
   * Render a full HTML document string into a PDF file download using html2pdf.js.
   */
  private async renderHtmlToPdf(fullHtml: string, filename: string): Promise<void> {
    // Lazy import html2pdf.js
    const html2pdf = (await import('html2pdf.js')).default;

    // Create a hidden container
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;';
    document.body.appendChild(container);

    // Parse HTML and inject into container
    const parser = new DOMParser();
    const doc = parser.parseFromString(fullHtml, 'text/html');
    // Inject styles into container
    const styles = doc.querySelectorAll('style');
    styles.forEach(s => container.appendChild(s.cloneNode(true)));
    // Inject body content
    if (doc.body) {
      container.innerHTML += doc.body.innerHTML;
    }

    try {
      const options: any = {
        margin: [10, 10, 10, 10],
        filename: filename.endsWith('.pdf') ? filename : `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], before: '.page-break' },
      };
      await html2pdf()
        .set(options)
        .from(container)
        .save();
    } finally {
      document.body.removeChild(container);
    }
  }

  // ── Lookup loading ────────────────────────────────────────────────────────

  private async loadLookups(): Promise<LookupMaps> {
    const [inspectors, sites, expertiseAreas, inspMethods, frequencies, users] = await Promise.all([
      apiService.getContractInspectors().catch(() => []),
      apiService.getContractSites().catch(() => []),
      apiService.getContractAreasOfExpertise().catch(() => []),
      apiService.getInspectionMethods().catch(() => []),
      apiService.getContractFrequencies().catch(() => []),
      apiService.getContractUsers().catch(() => []),
    ]);
    const toMap = <T extends { Id: number }>(arr: T[]) => new Map(arr.map(i => [i.Id, i]));
    return {
      inspectors: toMap(inspectors),
      sites: toMap(sites),
      expertiseAreas: toMap(expertiseAreas),
      inspectionMethods: toMap(inspMethods),
      frequencies: toMap(frequencies),
      users: toMap(users),
    };
  }

  // ── HTML generation ───────────────────────────────────────────────────────

  private buildReportHtml(
    lineItems: InspectionLineItemResponse[],
    contractDetails: ContractDetailsResponse | null,
    lookups: LookupMaps,
    options: ReportOptions,
  ): string {
    const sections: string[] = [];

    // Flatten: each line item may have multiple inspection results
    interface ReportItem { lineItem: InspectionLineItemResponse; result: InspectionResponse }
    const items: ReportItem[] = [];
    for (const li of lineItems) {
      for (const r of li.InspectionResults) {
        items.push({ lineItem: li, result: r });
      }
    }

    for (let idx = 0; idx < items.length; idx++) {
      const { lineItem, result } = items[idx];
      const sectionHtml = this.buildResultSection(lineItem, result, contractDetails, lookups, options);
      const pageBreak = idx < items.length - 1 ? ' class="page-break"' : '';
      sections.push(`<div${pageBreak}>${sectionHtml}</div>`);
    }

    return sections.join('\n');
  }

  private buildResultSection(
    lineItem: InspectionLineItemResponse,
    result: InspectionResponse,
    contractDetails: ContractDetailsResponse | null,
    lookups: LookupMaps,
    options: ReportOptions,
  ): string {
    const parts: string[] = [];

    // A. Header
    parts.push(this.renderHeader(contractDetails));

    // B. Title
    const title = this.getReportTitle(lineItem, result);
    parts.push(`<h2 class="report-title">${esc(title)}</h2>`);

    // C. Line Item Detail
    if (options.lineItemDetail) {
      parts.push(this.renderLineItemDetail(lineItem, contractDetails));
    }

    // D. Inspection Plan Background
    if (options.background) {
      parts.push(this.renderBackground(lineItem, lookups));
    }

    // E. Frequency
    if (options.frequency) {
      parts.push(this.renderFrequency(lineItem, lookups));
    }

    // F. Checklist Items (always included)
    parts.push(this.renderChecklistItems(lineItem, result, options));

    // G. Inspection Result Details (always)
    parts.push(this.renderResultDetails(result, lookups));

    // H. Inspection Approval
    if (!result.IsInProgress) {
      parts.push(this.renderApproval(result, lookups));
    }

    // I. Attachment Previews
    if (options.attachmentDescription) {
      parts.push(this.renderAttachmentPreviews(lineItem, result));
    }

    // J. Footer
    parts.push(this.renderFooter(contractDetails));

    return parts.join('\n');
  }

  // ── A. Header ─────────────────────────────────────────────────────────────

  private renderHeader(contractDetails: ContractDetailsResponse | null): string {
    const contractName = contractDetails?.Name ?? '';
    const logoUrl = contractDetails?.Logo;
    const contractLogoHtml = logoUrl
      ? `<img src="${esc(logoUrl)}" alt="Contract Logo" style="max-height:60px;max-width:200px;">`
      : `<span style="font-size:1.2rem;font-weight:700;color:#2c3e6b;">${esc(contractName)}</span>`;
    return `
      <div class="report-header">
        <div class="header-left">${contractLogoHtml}</div>
        <div class="header-right">
          <span style="font-size:1.4rem;font-weight:700;color:#2c3e6b;font-family:serif;">SeeSOR<sup style="font-size:0.6em;">®</sup></span>
        </div>
      </div>
      <div style="font-size:0.95rem;font-weight:600;color:#2c3e6b;margin-bottom:4px;">${esc(contractName)}</div>`;
  }

  // ── B. Title ──────────────────────────────────────────────────────────────

  private getReportTitle(lineItem: InspectionLineItemResponse, result: InspectionResponse): string {
    let title = 'Inspection Result';
    if (result.Id) title += ` ID ${result.Id}`;
    return `${title} - ${lineItem.LineItem} ${lineItem.Title}`;
  }

  // ── C. Line Item Detail ───────────────────────────────────────────────────

  private renderLineItemDetail(lineItem: InspectionLineItemResponse, contractDetails: ContractDetailsResponse | null): string {
    const col1 = contractDetails?.LineItemColumnTitle1 || 'Audit Type (EHS, OSHA Compliance…):';
    const col2 = contractDetails?.LineItemColumnTitle2 || 'Area of Audit (Lab, Office, W/house, Prod/Mfg…):';
    const col3 = contractDetails?.LineItemColumnTitle3 || 'TBD1:';
    const col4 = contractDetails?.LineItemColumnTitle4 || 'TBD2:';

    let html = `<h3 class="section-title">LINE ITEM DETAIL</h3>`;
    html += `<table class="detail-grid"><tr>
      <td><strong>${esc(col1)}</strong><br>${esc(lineItem.MajorRequirement ?? '')}</td>
      <td><strong>${esc(col2)}</strong><br>${esc(lineItem.RelatedInfo ?? '')}</td>
      <td><strong>${esc(col3)}</strong><br>${esc(lineItem.EstimatedQuantity ?? '')}</td>
      <td><strong>${esc(col4)}</strong><br>${esc(lineItem.PerformanceStandard ?? '')}</td>
    </tr></table>`;

    // Line Item Attachments
    const attachments = lineItem.LineItemAttachments ?? lineItem.Attachments ?? [];
    html += this.renderAttachmentTable('LINE ITEM ATTACHMENTS', attachments);

    return html;
  }

  // ── D. Background ─────────────────────────────────────────────────────────

  private renderBackground(lineItem: InspectionLineItemResponse, lookups: LookupMaps): string {
    const methodName = getLookupValue(lineItem.InspectionMethodId, lookups.inspectionMethods);
    const expertiseName = getLookupValue(lineItem.ExpertiseAreaId, lookups.expertiseAreas);

    let html = `<h3 class="section-title">INSPECTION PLAN BACKGROUND</h3>`;

    // Row 1
    html += `<table class="detail-grid"><tr>
      <td><strong>Line Item Primary Weight:</strong><br>${lineItem.PrimaryValue ?? ''}</td>
      <td><strong>Line Item Alternate Weight:</strong><br>${lineItem.AlternateValue ?? ''}</td>
      <td><strong>Hours to Inspect:</strong><br>${lineItem.TimeToInspect ?? ''}</td>
    </tr></table>`;

    // Row 2
    html += `<table class="detail-grid"><tr>
      <td><strong>Method of Inspection:</strong><br>${esc(methodName)}</td>
      <td><strong>Area of Expertise:</strong><br>${esc(expertiseName)}</td>
    </tr></table>`;

    // Row 3
    html += `<table class="detail-grid"><tr>
      <td><strong>Sample Size:</strong><br>${nl2br(lineItem.SampleSize ?? '')}</td>
      <td><strong>Sample Selection Procedure:</strong><br>${nl2br(lineItem.SampleSelection ?? '')}</td>
      <td><strong>Additional Instructions:</strong><br>${nl2br(lineItem.AdditionalInstructions ?? '')}</td>
    </tr></table>`;

    // Inspection Plan Attachments
    const attachments = lineItem.InspectionPlanAttachments ?? [];
    html += this.renderAttachmentTable('INSPECTION PLAN ATTACHMENTS', attachments);

    return html;
  }

  // ── E. Frequency ──────────────────────────────────────────────────────────

  private renderFrequency(lineItem: InspectionLineItemResponse, lookups: LookupMaps): string {
    let html = `<h3 class="section-title">FREQUENCY</h3>`;

    if (lineItem.AutoInspections !== null && lineItem.AutoInspections !== undefined) {
      // Auto-adjusted frequencies
      const auto = lineItem.AutoInspections;
      html += `<p><strong>Auto-adjusted Frequencies</strong></p>`;
      html += `<table class="data-table"><thead><tr>
        <th>Level</th><th>Frequency</th><th>Low</th><th>High</th><th>Triggers</th>
      </tr></thead><tbody>`;

      const normalFreq = getLookupValue(auto.NormalFrequencyId, lookups.frequencies);
      const decreasedFreq = getLookupValue(auto.DecreasedFrequencyId, lookups.frequencies);
      const increasedFreq = getLookupValue(auto.IncreasedFrequencyId, lookups.frequencies);

      html += `<tr><td>Normal</td><td>${esc(normalFreq)}</td><td>${auto.NormalLow ?? ''}</td><td>${auto.NormalHigh ?? ''}</td><td></td></tr>`;
      html += `<tr><td>Decreased</td><td>${esc(decreasedFreq)}</td><td>${auto.DecreasedLow ?? ''}</td><td></td><td></td></tr>`;
      html += `<tr><td>Increased</td><td>${esc(increasedFreq)}</td><td></td><td>${auto.IncreasedHigh ?? ''}</td><td></td></tr>`;
      html += `</tbody></table>`;
    } else {
      // Fixed Inspection Dates
      html += `<p><strong>Fixed Inspection Dates</strong></p>`;
      const fixedDates = [...(lineItem.FixedInspections ?? [])].sort((a, b) =>
        new Date(b.Date).getTime() - new Date(a.Date).getTime()
      );
      if (fixedDates.length === 0) {
        html += `<p class="text-muted">No scheduled dates</p>`;
      } else {
        html += `<div class="fixed-dates">`;
        for (const fd of fixedDates) {
          const annualIcon = fd.IsAnnual ? ' &#x1f501;' : '';
          html += `<div class="fixed-date">${formatDate(fd.Date)}${annualIcon}</div>`;
        }
        html += `</div>`;
      }
    }

    return html;
  }

  // ── F. Checklist Items ────────────────────────────────────────────────────

  private renderChecklistItems(
    lineItem: InspectionLineItemResponse,
    result: InspectionResponse,
    options: ReportOptions,
  ): string {
    let html = `<h3 class="section-title">CHECKLIST ITEMS</h3>`;

    // Build score map from result.ChecklistItems
    const scoreMap = new Map<number, InspectionChecklistItemResponse>();
    for (const ci of result.ChecklistItems ?? []) {
      scoreMap.set(ci.Id, ci);
    }

    // Use line item checklist items (sorted by Order)
    const checklistItems = [...(lineItem.ChecklistItems ?? [])].sort((a, b) => a.Order - b.Order);

    html += `<table class="data-table checklist-table"><thead><tr>
      <th style="width:40px;">#</th>
      <th>Question/Scoring Criteria</th>
      <th style="width:70px;">Weight</th>
      <th style="width:70px;">Score</th>
    </tr></thead><tbody>`;

    for (let i = 0; i < checklistItems.length; i++) {
      const cli = checklistItems[i];
      const resultCli = scoreMap.get(cli.Id);

      // Score display
      let scoreDisplay = 'N/A';
      if (resultCli && resultCli.Score !== null && resultCli.Score !== undefined) {
        scoreDisplay = resultCli.Score.toFixed(2);
      }

      // Text with optional scoring criteria
      let textHtml = esc(cli.Text);
      if (options.scoringCriteria && cli.ResultsAnalysis) {
        textHtml += `<br><em class="scoring-criteria">${esc(cli.ResultsAnalysis)}</em>`;
      }

      html += `<tr>
        <td class="num-cell"><strong>${i + 1}</strong></td>
        <td>${textHtml}</td>
        <td class="num-cell">${cli.Weight.toFixed(2)}</td>
        <td class="num-cell">${scoreDisplay}</td>
      </tr>`;

      // Comment
      if (options.checklistComments && resultCli?.Comment) {
        html += `<tr><td></td><td colspan="3"><div class="checklist-comment">${nl2br(resultCli.Comment)}</div></td></tr>`;
      }

      // Checklist item attachments table
      if (resultCli?.Attachments && resultCli.Attachments.length > 0) {
        html += `<tr><td></td><td colspan="3">`;
        html += `<table class="attachment-inline-table"><thead><tr><th>Attachment Description</th><th>File/URL</th></tr></thead><tbody>`;
        for (const att of resultCli.Attachments) {
          html += `<tr><td>${esc(att.Description)}</td><td>${esc(att.Filename)}</td></tr>`;
        }
        html += `</tbody></table></td></tr>`;
      }
    }

    html += `</tbody></table>`;
    return html;
  }

  // ── G. Inspection Result Details ──────────────────────────────────────────

  private renderResultDetails(result: InspectionResponse, lookups: LookupMaps): string {
    let html = '';

    // Weighted Average Score
    let scoreDisplay = 'N/A';
    if (result.CompositeScore !== null && result.CompositeScore !== undefined) {
      if (result.CompositeScore < 0) {
        scoreDisplay = 'In Progress';
      } else {
        scoreDisplay = result.CompositeScore.toFixed(2);
      }
    }

    const inspectorName = getLookupValue(result.UserId, lookups.inspectors)
      || getLookupValue(result.UserId, lookups.users);
    const siteName = getLookupValue(result.SiteId, lookups.sites);

    // Row 1
    html += `<table class="detail-grid" style="margin-top:24px;"><tr>
      <td><strong>Date:</strong><br>${formatDate(result.InspectedOn)}</td>
      <td><strong>Time:</strong><br>${formatTime(result.InspectedOn)}</td>
      <td><strong>Inspector:</strong><br>${esc(inspectorName)}</td>
      <td><strong>Site:</strong><br>${esc(siteName)}</td>
      <td><strong>Wtd Avg Score:</strong><br>${scoreDisplay}</td>
    </tr></table>`;

    // Row 2 - Audit trail
    const inputBy = getLookupValue(result.CreateUserId, lookups.users)
      || getLookupValue(result.CreateUserId, lookups.inspectors);
    const lastEditBy = getLookupValue(result.UpdateUserId, lookups.users)
      || getLookupValue(result.UpdateUserId, lookups.inspectors);

    html += `<table class="detail-grid"><tr>
      <td><strong>Input By:</strong><br>${esc(inputBy)}</td>
      <td><strong>Input On:</strong><br>${formatDate(result.CreateDate)}</td>
      <td><strong>Last Edit By:</strong><br>${esc(lastEditBy)}</td>
      <td><strong>Last Edit On:</strong><br>${formatDate(result.UpdatedDate ?? '')}</td>
    </tr></table>`;

    // Exceptional flag
    const checkedAttr = result.IsExceptional ? 'checked' : '';
    html += `<div style="margin:12px 0;"><label><input type="checkbox" ${checkedAttr} disabled> Flag this inspection as exceptional</label></div>`;

    // Comments
    html += `<h4 class="subsection-title">Inspection Comments:</h4>`;
    if (result.Comments) {
      html += `<p>${nl2br(result.Comments)}</p>`;
    }

    // Inspection Result Attachments table
    const attachments = result.Attachments ?? [];
    html += this.renderAttachmentTable('INSPECTION RESULT ATTACHMENTS', attachments);

    return html;
  }

  // ── H. Approval ───────────────────────────────────────────────────────────

  private renderApproval(result: InspectionResponse, lookups: LookupMaps): string {
    let html = `<h3 class="section-title">INSPECTION APPROVAL</h3>`;

    let status = 'None';
    if (result.IsApproved === true) status = 'Approved';
    else if (result.IsApproved === false) status = 'Disapproved';

    const approvedBy = getLookupValue(result.ApproveUserId, lookups.users)
      || getLookupValue(result.ApproveUserId, lookups.inspectors);

    html += `<p><strong>Status:</strong> ${status}</p>`;
    if (approvedBy) html += `<p><strong>Approved By:</strong> ${esc(approvedBy)}</p>`;
    if (result.ApproveDate) html += `<p><strong>Approved On:</strong> ${formatDate(result.ApproveDate)}</p>`;
    if (result.ApprovalComments) html += `<p><strong>Comments:</strong> ${nl2br(result.ApprovalComments)}</p>`;

    return html;
  }

  // ── I. Attachment Previews ────────────────────────────────────────────────

  private renderAttachmentPreviews(_lineItem: InspectionLineItemResponse, result: InspectionResponse): string {
    let html = '';

    // 1. Inspection Result Attachments
    const resultAttachments = result.Attachments ?? [];
    if (resultAttachments.length > 0) {
      html += `<h3 class="section-title">INSPECTION RESULT ATTACHMENTS</h3>`;
      html += this.renderAttachmentPreviews_inner(resultAttachments);
    }

    // 2. Checklist Items Attachments
    const checklistAttachments: { att: AttachmentResponse; order: number }[] = [];
    for (const ci of result.ChecklistItems ?? []) {
      if (ci.Attachments && ci.Attachments.length > 0) {
        for (const att of ci.Attachments) {
          checklistAttachments.push({ att, order: ci.Order });
        }
      }
    }
    if (checklistAttachments.length > 0) {
      html += `<h3 class="section-title">CHECKLIST ITEMS ATTACHMENTS</h3>`;
      for (const { att, order } of checklistAttachments) {
        const desc = `#${order} ${att.Description || ''}`;
        if (isImageFile(att.Filename)) {
          const previewUrl = apiService.generateAttachmentPreviewUrl(att.Id, att.FileId, 600);
          html += `<div class="attachment-preview">
            <img src="${esc(previewUrl)}" alt="${esc(desc)}" class="attachment-image" />
            <div class="attachment-filename">${esc(att.Filename)}</div>
            <div class="attachment-desc">${esc(desc)}</div>
            <hr>
          </div>`;
        } else {
          const icon = getFileTypeIcon(att.Filename, att.Link);
          html += `<div class="attachment-preview">
            <span style="font-size:2rem;">${icon}</span>
            <div class="attachment-filename">${esc(att.Filename)}</div>
            <div class="attachment-desc">${esc(desc)}</div>
            <hr>
          </div>`;
        }
      }
    }

    return html;
  }

  private renderAttachmentPreviews_inner(attachments: AttachmentResponse[]): string {
    let html = '';
    for (const att of attachments) {
      if (isImageFile(att.Filename)) {
        const previewUrl = apiService.generateAttachmentPreviewUrl(att.Id, att.FileId, 600);
        html += `<div class="attachment-preview">
          <img src="${esc(previewUrl)}" alt="${esc(att.Description)}" class="attachment-image" />
          <div class="attachment-filename">${esc(att.Filename)}</div>
          <div class="attachment-desc">${esc(att.Description)}</div>
          <hr>
        </div>`;
      } else {
        const icon = getFileTypeIcon(att.Filename, att.Link);
        html += `<div class="attachment-preview">
          <span style="font-size:2rem;">${icon}</span>
          <div class="attachment-filename">${esc(att.Filename)}</div>
          <div class="attachment-desc">${esc(att.Description)}</div>
          <hr>
        </div>`;
      }
    }
    return html;
  }

  // ── Attachment table helper ───────────────────────────────────────────────

  private renderAttachmentTable(title: string, attachments: AttachmentResponse[]): string {
    let html = `<h4 class="subsection-title">${esc(title)}</h4>`;
    if (!attachments || attachments.length === 0) {
      html += `<table class="data-table"><thead><tr><th>Description</th><th>File/URL</th></tr></thead><tbody>`;
      html += `<tr><td colspan="2" class="text-muted">No attachments</td></tr>`;
      html += `</tbody></table>`;
    } else {
      html += `<table class="data-table"><thead><tr><th>Description</th><th>File/URL</th></tr></thead><tbody>`;
      for (const att of attachments) {
        const fileRef = att.Link || att.Filename || '';
        html += `<tr><td>${esc(att.Description)}</td><td>${esc(fileRef)}</td></tr>`;
      }
      html += `</tbody></table>`;
    }
    return html;
  }

  // ── J. Footer ─────────────────────────────────────────────────────────────

  private renderFooter(contractDetails: ContractDetailsResponse | null): string {
    const footer = contractDetails?.Footer ?? '';
    return `
      <div class="report-footer">
        <hr>
        <div class="footer-text">${esc(footer)}</div>
      </div>`;
  }

  // ── Report Styles ─────────────────────────────────────────────────────────

  private getReportStyles(): string {
    return `
  @media print {
    @page { size: portrait; margin: 0.7in; }
    .page-break { page-break-after: always; }
  }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 0.85rem;
    color: #2c3e6b;
    line-height: 1.5;
    margin: 0;
    padding: 20px 40px;
  }
  .report-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 2px solid #2c3e6b;
    margin-bottom: 16px;
  }
  .header-left { display: flex; align-items: center; }
  .header-right { display: flex; align-items: center; }
  .report-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #2c3e6b;
    margin: 16px 0 12px;
  }
  .section-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #2c3e6b;
    text-transform: uppercase;
    margin: 20px 0 8px;
    letter-spacing: 0.5px;
  }
  .subsection-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #2c3e6b;
    margin: 16px 0 6px;
  }
  .detail-grid {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
  }
  .detail-grid td {
    padding: 8px 12px;
    vertical-align: top;
    font-size: 0.82rem;
  }
  .detail-grid strong {
    font-size: 0.78rem;
    display: block;
    margin-bottom: 2px;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
    font-size: 0.82rem;
  }
  .data-table th {
    text-align: left;
    padding: 6px 10px;
    font-weight: 700;
    border-bottom: 1px solid #2c3e6b;
    color: #2c3e6b;
  }
  .data-table td {
    padding: 6px 10px;
    vertical-align: top;
  }
  .data-table tbody tr:hover { background: #f5f6fa; }
  .num-cell { text-align: center; }
  .checklist-table td, .checklist-table th { border-bottom: none; }
  .checklist-table tbody tr:first-child td { padding-top: 10px; }
  .checklist-comment {
    padding: 6px 10px;
    border: 1px solid #c8c8d0;
    border-radius: 4px;
    font-size: 0.8rem;
    background: #fafbfc;
    margin: 2px 0 8px;
  }
  .attachment-inline-table {
    width: auto;
    min-width: 50%;
    border-collapse: collapse;
    font-size: 0.78rem;
    margin: 4px 0 10px;
    border: 1px solid #c8c8d0;
  }
  .attachment-inline-table th, .attachment-inline-table td {
    padding: 4px 10px;
    border: 1px solid #c8c8d0;
    text-align: left;
  }
  .attachment-inline-table th { font-weight: 600; background: #f5f6fa; }
  .text-muted { color: #888; font-style: italic; }
  .fixed-dates { margin: 6px 0 12px; }
  .fixed-date { padding: 4px 0; font-size: 0.85rem; }
  .attachment-preview { margin: 16px 0; }
  .attachment-image { max-width: 70%; height: auto; display: block; margin: 8px 0; }
  .attachment-filename { font-size: 0.82rem; color: #2c3e6b; margin: 4px 0; }
  .attachment-desc { font-size: 0.82rem; color: #2c3e6b; font-weight: 600; }
  .report-footer { margin-top: 30px; }
  .report-footer hr { border: none; border-top: 2px solid #2c3e6b; }
  .footer-text { text-align: center; font-size: 0.78rem; color: #666; margin-top: 8px; }
  .scoring-criteria { color: #666; font-size: 0.78rem; }
  @media print {
    body { padding: 0; }
    .attachment-image { max-width: 60%; }
  }`;
  }

  // ── Full HTML document builder ────────────────────────────────────────────

  private buildFullHtmlDocument(contentHtml: string, title: string): string {
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(title)}</title>
<style>${this.getReportStyles()}</style>
</head>
<body>
${contentHtml}
</body>
</html>`;
  }

  // ── Print window ──────────────────────────────────────────────────────────

  private openPrintWindow(contentHtml: string, title: string): void {
    const printWindow = window.open(
      '',
      '_blank',
      'toolbar=yes,location=no,directories=yes,menubar=yes,scrollbars=yes'
    );
    if (!printWindow) {
      throw new Error('Could not open print window. Please allow popups for this site.');
    }

    const fullHtml = this.buildFullHtmlDocument(contentHtml, title);
    printWindow.document.write(fullHtml);
    printWindow.document.close();
    printWindow.focus();
  }
}

export const reportGenerationService = new ReportGenerationService();
export default reportGenerationService;
